import { encodeKey, decodeKey } from "./PBKDF2";

/** Must match Java Util.HKDF_INFO_ECDH_AES */
export const HKDF_INFO_ECDH_AES = "Bayun-ECDH-AES-v1";

/** Must match Java Util.HKDF_INFO_XDH_AES */
export const HKDF_INFO_XDH_AES = "Bayun-XDH-AES-v1";

/** AES-256 / AEAD key size in bytes. */
export const AES_KEY_LENGTH_BYTES = 32;

const textEncoder = new TextEncoder();

const getSubtle = () => {
  const cryptoObj =
    globalThis.crypto || (typeof window !== "undefined" && window.crypto);
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new Error("Web Crypto subtle API is not available");
  }
  return cryptoObj.subtle;
};

/**
 * Normalize key/message input to Uint8Array.
 * Uint8Array / ArrayBuffer used as-is; strings → UTF-8 (Java parity).
 *
 * @param {Uint8Array|ArrayBuffer|string} value
 * @returns {Uint8Array}
 */
export const toBytes = (value) => {
  if (value == null) {
    throw new Error("toBytes: value is null or undefined");
  }
  if (value instanceof Uint8Array) {
    return value;
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView && ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (typeof value === "string") {
    return textEncoder.encode(value);
  }
  throw new Error("toBytes: unsupported type");
};

/**
 * Coerce stored/wire key material to raw 32-byte key.
 * Uint8Array/ArrayBuffer returned as-is; Base64 string → decodeKey.
 *
 * @param {Uint8Array|ArrayBuffer|string|null} value
 * @returns {Uint8Array|null}
 */
export const asRawKey = (value) => {
  if (value == null || value === "") {
    return null;
  }
  if (
    value instanceof Uint8Array ||
    value instanceof ArrayBuffer ||
    (typeof ArrayBuffer !== "undefined" &&
      ArrayBuffer.isView &&
      ArrayBuffer.isView(value))
  ) {
    return toBytes(value);
  }
  if (typeof value === "string") {
    return decodeKey(value);
  }
  return null;
};

/**
 * HMAC-SHA256 over message with key. Returns full 32-byte digest (no Base64 truncation).
 * Matches Java Util.hmacSha256Raw. For wire/storage use encodeKey.
 *
 * @param {Uint8Array|ArrayBuffer|string} key
 * @param {Uint8Array|ArrayBuffer|string} message
 * @returns {Promise<Uint8Array>}
 */
export const hmacSha256Raw = async (key, message) => {
  const subtle = getSubtle();
  const keyBytes = toBytes(key);
  const messageBytes = toBytes(message);

  const cryptoKey = await subtle.importKey(
    "raw",
    keyBytes,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const digest = await subtle.sign("HMAC", cryptoKey, messageBytes);
  return new Uint8Array(digest);
};

/**
 * HKDF-HMAC-SHA256 (RFC 5869 Extract+Expand). Returns `length` raw bytes.
 * Empty/null salt ⇒ 32 zero bytes (matches Java Util.hkdfSha256 / Web Crypto).
 * Raw output is canonical; Base64 only via encodeKey.
 *
 * @param {Uint8Array|ArrayBuffer|string} ikm
 * @param {Uint8Array|ArrayBuffer|string|null} salt
 * @param {Uint8Array|ArrayBuffer|string|null} info
 * @param {number} length
 * @returns {Promise<Uint8Array>}
 */
export const hkdfSha256Raw = async (ikm, salt, info, length = AES_KEY_LENGTH_BYTES) => {
  const subtle = getSubtle();
  const ikmBytes = toBytes(ikm);
  const saltBytes =
    salt == null || (salt instanceof Uint8Array && salt.length === 0) || salt === ""
      ? new Uint8Array(32)
      : toBytes(salt);
  const infoBytes =
    info == null || info === ""
      ? new Uint8Array(0)
      : toBytes(info);

  // Prefer Web Crypto HKDF when available
  if (typeof subtle.importKey === "function") {
    try {
      const baseKey = await subtle.importKey("raw", ikmBytes, "HKDF", false, [
        "deriveBits",
      ]);
      const bits = await subtle.deriveBits(
        {
          name: "HKDF",
          hash: "SHA-256",
          salt: saltBytes,
          info: infoBytes,
        },
        baseKey,
        length * 8
      );
      return new Uint8Array(bits);
    } catch (e) {
      // Fall through to manual HMAC extract/expand
    }
  }

  return hkdfSha256Manual(ikmBytes, saltBytes, infoBytes, length);
};

/**
 * Manual RFC 5869 HKDF-SHA256 (matches Java Util extract/expand).
 */
const hkdfSha256Manual = async (ikm, salt, info, length) => {
  const prk = await hmacSha256Raw(salt, ikm);
  const hashLen = 32;
  const n = Math.ceil(length / hashLen);
  const okm = new Uint8Array(n * hashLen);
  let previous = new Uint8Array(0);

  for (let i = 1; i <= n; i++) {
    const input = new Uint8Array(previous.length + info.length + 1);
    input.set(previous, 0);
    input.set(info, previous.length);
    input[previous.length + info.length] = i;
    previous = await hmacSha256Raw(prk, input);
    okm.set(previous, (i - 1) * hashLen);
  }

  return okm.slice(0, length);
};

/**
 * @deprecated Use hmacSha256Raw for AES keys, or encodeKey(hmacSha256Raw(...)) for wire.
 * Returns full Base64 of 32-byte HMAC (no truncation) — matches Java generateHMACKey.
 *
 * @param {Uint8Array|ArrayBuffer|string} key
 * @param {Uint8Array|ArrayBuffer|string} message
 * @returns {Promise<string>}
 */
export const generateHMacHashEncoded = async (key, message) => {
  return encodeKey(await hmacSha256Raw(key, message));
};
